// Credit note detail (BUILD_SPEC.md §8.4 step 5). Plan.md G23. DRAFT -> Approve
// (books RETURN_IN movements when restock = true, renders the PDF, marks Xero
// pending); APPROVED -> Void (blocked server-side when the note restocked —
// services/api's credit-notes.ts `/void` handler documents why: undo a restock
// with an explicit correcting document, never a silent side effect of a status
// flip — the button is still offered, the server's 409 message explains it).
import { Alert, Button, Divider, Group, Loader, Paper, Stack, Table, Text, Title, Tooltip } from '@mantine/core';
import { Link } from '@tanstack/react-router';
import { IconAlertTriangle, IconCheck } from '@tabler/icons-react';
import { ApiRequestError } from '../../api/client';
import { useAuth } from '../../auth/AuthContext';
import { ConfirmButton } from '../../components/ConfirmButton';
import { DocStatusBadge } from '../../components/DocStatusBadge';
import { LocationName, ProductName } from './components/EntityNames';
import { DownloadPdfLink } from './components/DownloadPdfLink';
import { formatMoneyCents, formatQty } from './format';
import { useApproveCreditNote, useCreditNote, useInvoice, useVoidCreditNote } from './api';

export function CreditNoteDetail({ creditNoteId }: { creditNoteId: string }) {
  const auth = useAuth();
  const canInvoice = auth.activeTenant?.role === 'SALES_ACCOUNTS' || auth.activeTenant?.role === 'ADMIN';
  const { data: cn, isLoading, isError } = useCreditNote(creditNoteId);
  const invoice = useInvoice(cn?.invoiceId);
  const approve = useApproveCreditNote(creditNoteId);
  const voidCn = useVoidCreditNote(creditNoteId);

  if (isLoading) return <Loader />;
  if (isError || !cn) {
    return (
      <Alert color="red" icon={<IconAlertTriangle size={18} />}>
        Credit note not found.
      </Alert>
    );
  }

  const actionError = approve.error ?? voidCn.error;

  return (
    <Stack gap="lg" maw={860}>
      <Group justify="space-between">
        <Group gap="sm">
          <Title order={2}>{cn.number}</Title>
          <DocStatusBadge status={cn.status} domain="creditNote" />
        </Group>
        <Button component={Link} to="/sales-invoicing/credit-notes" variant="subtle">
          Back to register
        </Button>
      </Group>

      {/* Framing line + the void rule stated UPFRONT (goal: don't let a user
          discover this via a server 409) — Approve books the restock (if any)
          and notifies Xero; once approved, a credit note that restocked goods
          can never be voided, only corrected with a new document. */}
      <Text c="dimmed" size="sm">
        Credits <strong>{formatMoneyCents(cn.totalCents)}</strong> against invoice {invoice.data?.number ?? cn.invoiceId}.
        Approving it {cn.restock ? 'restocks the goods and ' : ''}notifies Xero — a credit note that has restocked goods
        can&apos;t be voided afterwards, only corrected with a new one.
      </Text>

      {(approve.isError || voidCn.isError) && (
        <Alert color="red" icon={<IconAlertTriangle size={18} />}>
          {actionError instanceof ApiRequestError ? actionError.message : 'Something went wrong.'}
        </Alert>
      )}

      <Paper withBorder radius="md" p="md">
        <Stack gap="xs">
          <Group gap={6}>
            <Text fw={600}>Invoice:</Text>
            <Link to="/sales-invoicing/invoices/$invoiceId" params={{ invoiceId: cn.invoiceId }}>
              {invoice.data?.number ?? cn.invoiceId}
            </Link>
          </Group>
          {cn.reason && (
            <Group gap={6} align="flex-start">
              <Text fw={600}>Reason:</Text>
              <Text>{cn.reason}</Text>
            </Group>
          )}
          <Group gap={6}>
            <Text fw={600}>Restock:</Text>
            <Text>{cn.restock ? 'Yes' : 'No'}</Text>
          </Group>
        </Stack>
      </Paper>

      <Paper withBorder radius="md" p="md">
        <Table.ScrollContainer minWidth={640}>
          <Table verticalSpacing="xs">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Product</Table.Th>
                <Table.Th>Description</Table.Th>
                <Table.Th>Qty</Table.Th>
                <Table.Th ta="right">Unit price</Table.Th>
                <Table.Th ta="right">Line total</Table.Th>
                {cn.restock && <Table.Th>Restock location</Table.Th>}
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {cn.lines.map((line) => (
                <Table.Tr key={line.id}>
                  <Table.Td>
                    <ProductName id={line.productId} />
                  </Table.Td>
                  <Table.Td>{line.description}</Table.Td>
                  <Table.Td>{formatQty(line.qty)}</Table.Td>
                  <Table.Td ta="right">{formatMoneyCents(line.unitPriceCents)}</Table.Td>
                  <Table.Td ta="right">{formatMoneyCents(line.lineTotalCents)}</Table.Td>
                  {cn.restock && <Table.Td>{line.locationId ? <LocationName id={line.locationId} /> : '—'}</Table.Td>}
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
        <Divider my="sm" />
        <Stack gap={2} align="flex-end">
          <Text size="sm">Subtotal: {formatMoneyCents(cn.subtotalCents)}</Text>
          <Text size="sm">VAT: {formatMoneyCents(cn.vatCents)}</Text>
          <Text fw={700} size="lg">
            Total: {formatMoneyCents(cn.totalCents)}
          </Text>
        </Stack>
      </Paper>

      <Group>
        <DownloadPdfLink label="Download credit note PDF" pdfKey={cn.pdfKey} downloadPath={`/invoicing/credit-notes/${cn.id}/pdf`} />
      </Group>

      {/* Ledger-effect preview before the posting action (§11 convention —
          every posting screen shows what Post/Approve will do to the ledger
          BEFORE the click; this was the one posting screen without it, audit
          fix 2026-07-07). Mirrors GoodsReceiptDraftModal's/LandedCostFormModal's
          green-panel idiom. */}
      {cn.status === 'DRAFT' && (
        <Paper withBorder p="sm" radius="md" bg="var(--mantine-color-green-light)">
          <Stack gap={4}>
            <Text size="sm" fw={600}>
              Ledger effect on Approve
            </Text>
            {cn.restock ? (
              cn.lines.map((line) => (
                <Text size="sm" key={line.id}>
                  Stock RETURN_IN: <strong>+{formatQty(line.qty)}</strong> <ProductName id={line.productId} /> into{' '}
                  {line.locationId ? <LocationName id={line.locationId} /> : '—'}.
                </Text>
              ))
            ) : (
              <Text size="sm">No stock movement — restock is off, so this credit note only credits money.</Text>
            )}
            <Text size="sm">
              Customer credited <strong>{formatMoneyCents(cn.totalCents)}</strong> (incl VAT) against invoice{' '}
              {invoice.data?.number ?? cn.invoiceId}.
            </Text>
          </Stack>
        </Paper>
      )}

      {canInvoice && (
        <Group justify="flex-end">
          {cn.status === 'DRAFT' && (
            <Tooltip label="Approves this credit note — locks it, credits the customer, issues the PDF, and books a stock return if restock is on.">
              <Button leftSection={<IconCheck size={16} />} loading={approve.isPending} onClick={() => approve.mutate(undefined)}>
                Approve
              </Button>
            </Tooltip>
          )}
          {cn.status === 'APPROVED' && (
            <ConfirmButton
              color="red"
              variant="light"
              loading={voidCn.isPending}
              label="Void"
              message={`Void credit note ${cn.number}? It will be excluded from Xero sync. Blocked if it already restocked — post a new correcting document instead.`}
              onConfirm={() => voidCn.mutate(undefined)}
            />
          )}
        </Group>
      )}
    </Stack>
  );
}
